import { CopyReportPayload, CopyReportResponse, GetReportResponse, RemoveReportApiResponse } from './../@core/interfaces/dataset-details.interface';
import {
  Aggregations,
  CardinalityAggs,
  OntologyFilterPayload,
  FilterAggregations,
  OntologyFilterResponse,
  NestedAggregations,
} from '../../../@core/interfaces/dataset-filters.interface';
import {
  RequestDatasetResponse,
  RequestDatasetPayload,
  ProcessedDataQueryResponse,
  ProcessedDataResponseV2,
  ExplorePayload,
  ExploreResponseData,
  StringToStringObjectMapping,
  GDXSchemaType,
} from './../../../@core/interfaces/omix-atlas.interface';
import { ApiEndPoints } from '../../../polly-manage.constants';
import {
  DatalakeApiService,
  DiscoverApiService,
  ElasticApiResponse,
  ElasticFilters,
  OmixatlasApiService,
  ElasticCountApiResponse,
  DatasetSourceV2,
  DatasetStudies,
  CommonApiService
} from 'polly-commons';
import { ElasticQueryDownloadInterface, ElasticSearchPayload } from '../../../@core/interfaces/elastic-query.interface';
import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { atlasApiEndpoint } from '../constants/omixatlas.constants';
import { OmixAtlasesSummaryResponse, ProcessedDataResponse } from '../../../@core/interfaces/omix-atlas.interface';
import { BuyDatasetPayload, BuyDatasetResponse, CopyDatasetPayload, CopyDatasetResponse, DatasetDownloadResponse, IndividualRepositoryResponse, RepositoryListResponseV2, PaginatorType, SourceAtlasResponse } from '../@core/interfaces';
import { SchemaApiResponseInterface, SchemaMetadataUrlInterface } from '../../../@core/interfaces/api-schema.interface';
import { ApiCachingService } from './polly-omixatlas-dexie.service';
import { QueryDownloadFileResponse } from '../../../@core/interfaces/apis.interface';

@Injectable({
  providedIn: 'root',
})
export class PollyOmixatlasDataService {
  constructor(
    private readonly discoverService: DiscoverApiService,
    private readonly datalakeApi: DatalakeApiService,
    private readonly cacheService: ApiCachingService,
    private readonly omixatlasApi: OmixatlasApiService,
    private readonly commonApiService: CommonApiService
  ) {}

  public getDatasetsFilters(
    indexName: string,
    payload: ElasticSearchPayload
  ): Observable<ElasticApiResponse<undefined, FilterAggregations>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public fetchFilterSchema(repoId: string | undefined): Observable<SchemaApiResponseInterface> {
    const url = `/repositories/${repoId}/schemas/files`;
    return from(this.cacheService.cacheApiCall(url, () => this.discoverService.get(url).toPromise()));
  }

  public fetchMetadataTableSchema(repoId: string | undefined, apiEndPoint: string): Observable<SchemaApiResponseInterface> {
    const url = `/repositories/${repoId}/schemas/${apiEndPoint}`;
    return from(this.cacheService.cacheApiCall(url, () => this.discoverService.get(url).toPromise()));
  }

  public getDatasets(indexName: string, payload: ElasticFilters): Observable<ElasticApiResponse<DatasetSourceV2, undefined>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public getDatasetCount(indexName: string, payload: ElasticFilters): Observable<ElasticCountApiResponse> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_count`, payload);
  }

  public getAllOtherDataset(indexName: string, payload: ElasticSearchPayload): Observable<DatasetStudies> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public getDatasetsExplore(repoId: string, payload: ExplorePayload, paginationData?: PaginatorType): Observable<ExploreResponseData> {
    let url =  `${atlasApiEndpoint.GET_OMIXATLASES}/${repoId}/explore`;
    if (paginationData) {
      url += `?page[size]=${paginationData.rows}&page[number]=${paginationData.page}`;
    }
    return this.omixatlasApi.post(url, payload);
  }

  public getDatasetsWithAggregations(
    indexName: string,
    payload: ElasticFilters
  ): Observable<ElasticApiResponse<DatasetSourceV2, Aggregations>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public getDatasetsWithNestedAggregations(
    indexName: string,
    payload: ElasticFilters
  ): Observable<ElasticApiResponse<DatasetSourceV2, NestedAggregations>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public getRepositories(): Observable<RepositoryListResponseV2> {
    return this.discoverService.get(ApiEndPoints.GET_REPOSITORIES);
  }

  public getOmixatlases(): Observable<RepositoryListResponseV2> {
    const url = atlasApiEndpoint.GET_OMIXATLASES;
    return from(this.cacheService.cacheApiCall(url, () => this.omixatlasApi.get(url).toPromise()));
  }
  public getAuditSourceAtlas(): Observable<SourceAtlasResponse> {
    return this.commonApiService.get(atlasApiEndpoint.GET_AUDIT_SOURCE_ATLAS);
  }
  public getRepositoryDetails(indexName: string, payload: object): Observable<ElasticApiResponse<[], CardinalityAggs>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }
  public getSummarisedOmixatlases(): Observable<OmixAtlasesSummaryResponse> {
    const url = `${atlasApiEndpoint.GET_OMIXATLASES}?summarize=true&v2=true`;
    return from(this.cacheService.cacheApiCall(url, () => this.omixatlasApi.get(url).toPromise()));
  }

  public postNewDataset(payload: RequestDatasetPayload): Observable<RequestDatasetResponse> {
    return this.omixatlasApi.post(`${atlasApiEndpoint.GET_OMIXATLASES}/dataset-requests`, payload);
  }

  public getProcessedData(indexName: string, payload: ElasticFilters): Observable<ElasticApiResponse<ProcessedDataResponse, undefined>> {
    return this.datalakeApi.post(`elastic/v2/${indexName}/_search`, payload);
  }

  public downloadDataset(repoId: string, datasetId: string): Observable<DatasetDownloadResponse> {
    return this.omixatlasApi.get(`${atlasApiEndpoint.GET_OMIXATLASES}/${repoId}/download?_id=${datasetId}&v2=true`);
  }

  public getProcessedQueryRunning(payload: any): Observable<ProcessedDataQueryResponse> {
    return this.omixatlasApi.post(`${atlasApiEndpoint.GET_OMIXATLASES}/queries`, payload);
  }
  public getProcessedDataSucceed(queryId: string): Observable<ProcessedDataQueryResponse> {
    return this.omixatlasApi.get(`${atlasApiEndpoint.GET_OMIXATLASES}/queries/${queryId}`);
  }

  public getProcessedDataResults(queryId: string): Observable<ProcessedDataResponseV2> {
    return this.omixatlasApi.get(`${atlasApiEndpoint.GET_OMIXATLASES}/queries/${queryId}/results`);
  }
  public getNextProcessedData(url: string): Observable<ProcessedDataResponseV2> {
    return this.omixatlasApi.get(url);
  }
  public getOntologies(repoId: string, payload: OntologyFilterPayload, gdxSchemaType?: GDXSchemaType): Observable<OntologyFilterResponse<undefined, Aggregations>> {
    let url = `${atlasApiEndpoint.GET_OMIXATLASES}/${repoId}/autocomplete`;
    if (!!gdxSchemaType) {
      url += `?repo_schema=${gdxSchemaType}&gdx=true`;
    }
    return this.omixatlasApi.post(url, payload);
  }

  public copyToWorkspaces(payload: CopyDatasetPayload): Observable<CopyDatasetResponse> {
    return this.omixatlasApi.post(`${atlasApiEndpoint.GET_OMIXATLASES}/workspace_jobs?action=copy&v2=true`, payload);
  }

  public getOmixatlasInfo(repoId: string): Observable<IndividualRepositoryResponse> {
    return this.omixatlasApi.get(`${atlasApiEndpoint.GET_OMIXATLASES}/${repoId}`);
  }

  public buyDatasetData(payload: BuyDatasetPayload): Observable<BuyDatasetResponse> {
    return this.discoverService.post('repositories/ingestion-transactions', payload);
  }

  public addReportToDataset(repoId: string, payload: CopyReportPayload): Observable<CopyReportResponse> {
    return this.omixatlasApi.post(`v1/omixatlases/${repoId}/reports`, payload);
  }
  public getDatasetReport(atlasId: string, datasetId: string): Observable<GetReportResponse> {
    return this.omixatlasApi.get(`v1/omixatlases/${atlasId}/reports?dataset_id=${datasetId}`);
  }

  public removeDatasetReport(atlasId: string, datasetId: string, reportId: string): Observable<RemoveReportApiResponse> {
    return this.omixatlasApi.delete(`v1/omixatlases/${atlasId}/reports?dataset_id=${datasetId}&report_id=${reportId}`);
  }

  public fetchMetadataSchemaUrl(repoId: string): Observable<SchemaMetadataUrlInterface> {
    const url = `/repositories/${repoId}/schemas?meta=true`;
    return from(this.cacheService.cacheApiCall(url, () => this.discoverService.get(url).toPromise()));
  }

  public getGDXSchema(repoId: string,schemaType: string): Observable<SchemaApiResponseInterface> {
    return this.discoverService.get(`repositories/${repoId}/schemas/${schemaType}`);
  }

  public getGDXComparisonData(repoName: string,payload: ElasticFilters, schemaType: string = 'comparison_data'): Observable<ElasticApiResponse<StringToStringObjectMapping,Aggregations>> {
    return this.datalakeApi.post(`elastic/v2/${repoName}_${schemaType}/_search/?gdx=true`, payload);
  }

  public createDownloadElasticQuery(downloadPayload: ElasticQueryDownloadInterface): Observable<ProcessedDataQueryResponse> {
    return this.discoverService.post('/repositories/tasks', downloadPayload);
  }

  public checkDownloadElasticQueryStatus(taskID: string): Observable<ProcessedDataQueryResponse> {
    return this.discoverService.get(`/repositories/tasks/${taskID}`);
  }

  public getElasticDownloadSignedURL(taskID: string): Observable<QueryDownloadFileResponse> {
    return this.discoverService.get(`/repositories/tasks/${taskID}/results`);
  }
}
